import { NgTemplateOutlet } from '@angular/common';
import type { QueryList, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';

import { PushPipe } from '@ngrx/component';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { filterBoolean } from '@recruitee/common';
import {
  EmailTemplatesEventInvitationActions,
  EmailTemplatesEventInvitationSelectors,
  EmailTemplatesGeneralActions,
  EmailTemplatesGeneralSelectors,
} from '@recruitee/email-templates-data';
import type {
  EmailTemplate,
  EmailTemplatesEventInvitation,
} from '@recruitee/email-templates-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  DialogRef,
  KeyCode,
  RtStatefulComponent,
  ButtonComponent,
  EmptyStateComponent,
  IconComponent,
  DividerComponent,
  DropdownItemComponent,
  DropdownHeaderComponent,
} from '@recruitee/user-interface';
import { includesWithTrim } from '@recruitee/utils';
import type { Visibility } from '@recruitee/visibility-types';
import { VisibilityLevel } from '@recruitee/visibility-types';

import { RedactorInsertsDropdownHeaderComponent } from '../redactor-inserts-dropdown-header/redactor-inserts-dropdown-header.component';

type State = {
  emailTemplates: InsertsEmailTemplate[];
  emailTemplateGroups: TemplateGroup[];
  searchQuery: string;
  selectedId: number | string | null;
  hasFocus: boolean;
};

type TemplateGroup = {
  groupName: string;
  groupedEmailTemplates: EmailTemplate[];
};

export interface InsertsEmailTemplate extends Pick<EmailTemplate, 'id' | 'title'> {
  visibility?: Visibility;
}

export enum InsertsEmailKind {
  EmailTemplate = 'email-templates',
  EventInvitation = 'event-invitation-templates',
}

@Component({
  selector: 'rt-redactor-inserts-email-templates',
  templateUrl: './redactor-inserts-email-templates.component.html',
  styleUrls: ['./redactor-inserts-email-templates.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RedactorInsertsDropdownHeaderComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    DividerComponent,
    IconComponent,
    EmptyStateComponent,
    ButtonComponent,
    NgTemplateOutlet,
    TranslatePipe,
    PushPipe,
  ],
})
export class RedactorInsertsEmailTemplatesComponent extends RtStatefulComponent<State> {
  public readonly InsertsEmailKind = InsertsEmailKind;

  @Input() public inside: boolean;
  @Input() public insideTemplate?: TemplateRef<any>;
  @Input() public hasBackAction: boolean = true;
  @Input() public kind: InsertsEmailKind;

  @Output() public back: EventEmitter<void> = new EventEmitter();
  @Output() public settings: EventEmitter<string> = new EventEmitter();
  @Output() public emailTemplateChoose: EventEmitter<
    EmailTemplate | EmailTemplatesEventInvitation
  > = new EventEmitter();
  @Output() public closeDropdown: EventEmitter<void> = new EventEmitter();
  @Output() public saveAsTemplate = new EventEmitter<void>();

  @ViewChildren('eleRef', { read: ElementRef })
  public items: QueryList<ElementRef>;
  public canManageTemplates$: Observable<boolean> = this.store.select(
    AbilitiesSelectors.hasOne(Ability.ManageEmailTemplates),
  );

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    protected cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private router: Router,
    @Optional() private dialogRef: DialogRef,
    private actions$: Actions,
    private store: Store,
  ) {
    super(cdRef);
    this.createState({
      emailTemplates: [],
      emailTemplateGroups: [],
      searchQuery: '',
      selectedId: null,
      hasFocus: false,
    });
  }

  // eslint-disable-next-line max-lines-per-function
  public ngOnInit(): void {
    const isEmailTemplate = this.kind === InsertsEmailKind.EmailTemplate;

    isEmailTemplate
      ? this.store.dispatch(EmailTemplatesGeneralActions.fetchCollection())
      : this.store.dispatch(
          EmailTemplatesEventInvitationActions.fetchCollection({ payload: { scope: 'main' } }),
        );

    const data$: Observable<InsertsEmailTemplate[] | null> = isEmailTemplate
      ? this.store.select(EmailTemplatesGeneralSelectors.entities())
      : this.store.select(EmailTemplatesEventInvitationSelectors.entities({ scope: 'main' }));
    data$
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe((emailTemplates: InsertsEmailTemplate[]) => {
        const groupedEmailTemplates = this.groupAndFilter(emailTemplates!, this.state.searchQuery);

        this.setState({
          ...this.state,
          emailTemplateGroups: groupedEmailTemplates,
          emailTemplates: emailTemplates!,
          selectedId: null,
        });
      });

    this.state$
      .pipe(
        map(state => state.selectedId),
        distinctUntilChanged(),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(selectedId => {
        if (this.items) {
          this.items.forEach(item => {
            const nativeElement = item.nativeElement;
            if (nativeElement) {
              const id = parseInt(nativeElement.getAttribute('data-id'), 10);
              if (id === selectedId) {
                nativeElement.scrollIntoView();
              }
            }
          });
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public templatesTrackByFn(_index: number, template: EmailTemplate) {
    return template.id;
  }

  public groupsTrackByFn(_index: number, group: TemplateGroup): string {
    return group.groupName;
  }

  public goToPage(link: string): void {
    if (this.dialogRef) {
      this.dialogRef.afterClosed.subscribe(() => {
        this.router.navigate([link]);
      });

      this.dialogRef.close();
    } else {
      this.router.navigate([link]);
    }
  }

  public goBack(): void {
    this.back.emit();
  }
  public goToSettings(url: string): void {
    this.settings.emit(url);
  }

  public onSearchQueryChange(searchQuery: string): void {
    this.setState({
      ...this.state,
      emailTemplateGroups: this.groupAndFilter(this.state.emailTemplates, searchQuery),
      searchQuery,
    });
  }

  // eslint-disable-next-line max-lines-per-function
  public handleKeyDown(event: KeyboardEvent): void {
    const key = event.which || event.keyCode;

    switch (key) {
      case KeyCode.Enter: {
        event.preventDefault();
        if (this.state.selectedId === null) {
          return;
        } else {
          const filteredTemplates = this.flatFilteredTemplateGroups(this.state.emailTemplateGroups);
          const template = <EmailTemplate | EmailTemplatesEventInvitation>(
            filteredTemplates.find(temp => temp.id === this.state.selectedId)!
          );
          this.emailTemplateChoose.emit(template);
        }
        break;
      }

      case KeyCode.Backspace:
        if (!this.state.hasFocus || this.state.searchQuery.length === 0) {
          event.preventDefault();
          this.back.emit();
        }
        break;

      case KeyCode.UpArrow: {
        event.preventDefault();
        const filteredTemplates = this.flatFilteredTemplateGroups(this.state.emailTemplateGroups);
        const index = filteredTemplates.findIndex(temp => temp.id === this.state.selectedId);
        if (index <= 0) {
          this.setState({
            ...this.state,
            selectedId: filteredTemplates[filteredTemplates.length - 1].id!,
          });
        } else {
          this.setState({ ...this.state, selectedId: filteredTemplates[index - 1].id! });
        }
        break;
      }

      case KeyCode.DownArrow: {
        event.preventDefault();
        const filteredTemplates = this.flatFilteredTemplateGroups(this.state.emailTemplateGroups);
        const index = filteredTemplates.findIndex(temp => temp.id === this.state.selectedId);
        if (index === filteredTemplates.length - 1 || index === -1) {
          this.setState({
            ...this.state,
            selectedId: filteredTemplates[0].id!,
          });
        } else {
          this.setState({ ...this.state, selectedId: filteredTemplates[index + 1].id! });
        }
        break;
      }

      default:
        return;
    }
  }

  public onFocusChanges(hasFocus: boolean): void {
    this.setState({
      ...this.state,
      hasFocus,
    });
  }

  private flatFilteredTemplateGroups(
    groupedEmailTemplates: TemplateGroup[],
  ): InsertsEmailTemplate[] {
    return groupedEmailTemplates.reduce<InsertsEmailTemplate[]>(
      (acc, group) => acc.concat(group.groupedEmailTemplates),
      [],
    );
  }

  private groupAndFilter(
    emailTemplates: InsertsEmailTemplate[],
    searchQuery: string,
  ): TemplateGroup[] {
    const query = searchQuery.toLowerCase();
    const filteredTemplates = emailTemplates.filter(emailTemplate =>
      includesWithTrim(emailTemplate.title, query),
    );
    const companyTemplates = filteredTemplates.filter(
      template => template.visibility?.level !== VisibilityLevel.Private,
    );
    const personalTemplates = filteredTemplates.filter(
      template => template.visibility?.level === VisibilityLevel.Private,
    );

    return [
      ...(companyTemplates.length
        ? [
            {
              groupName: this.i18n.translate(`app.email_templates.company_sidebar_category`),
              groupedEmailTemplates: <EmailTemplate[]>companyTemplates,
            },
          ]
        : []),
      ...(personalTemplates.length
        ? [
            {
              groupName: this.i18n.translate(`app.email_templates.personal_sidebar_category`),
              groupedEmailTemplates: <EmailTemplate[]>personalTemplates,
            },
          ]
        : []),
    ];
  }
}
